import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Run one async action at a time, and say while it is running.
 *
 * ═══ WHY THIS EXISTS ═══
 *
 * A tap that fires a request and leaves the control enabled reads as "nothing
 * happened", so people tap again. On 2026-09-20 a scan of the three apps found
 * 33 such controls — securing a Session, accepting a Trade, starting a Rider
 * Session, submitting Report an Issue, removing a GiftPass — none of which
 * stopped a second tap or showed progress. The payment paths were safe only
 * because each had hand-rolled its own `running` ref.
 *
 * `run` ignores a second call while the first is in flight (the ref, not the
 * state, because two taps inside one render would both see `busy === false`),
 * and `busy` drives `<Button loading>`.
 *
 * It does NOT swallow errors: the caller still decides what a failure looks
 * like, which is usually a toast naming what the server refused. It only
 * guarantees `busy` returns to false afterwards.
 */
export function useAsyncAction<Args extends unknown[]>(
  action: (...args: Args) => Promise<unknown>,
): { busy: boolean; run: (...args: Args) => Promise<void> } {
  const [busy, setBusy] = useState(false);
  const running = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const run = useCallback(
    async (...args: Args) => {
      if (running.current) return;
      running.current = true;
      setBusy(true);
      try {
        await action(...args);
      } finally {
        running.current = false;
        // A sheet that closes on success unmounts before this runs; setting
        // state then is a React warning and nothing else, so it is skipped.
        if (mounted.current) setBusy(false);
      }
    },
    [action],
  );

  return { busy, run };
}
